// The Android local-database engine lifecycle: migrate to SQLite, roll back off it
// again, and count the grace period before the old IndexedDB copy is deleted.
// (#4981 phases 3 and 4 — #5269, #5448, #5449, #5270.)
//
// **Why this is a module and not part of `AppComponent`.** All of it used to live on the
// root component, which cannot be built in a unit test — it has dozens of injected
// services — so none of this logic could be driven by one. The tests for it were source
// TRIPWIRES instead: they read `app.component.ts` as text and matched patterns in it.
// Each phase had to widen those patterns because a call grew an argument or a comment
// moved a field, and a widened tripwire pins less than it claims to. Deferred finding
// `df-40735e5e8e9c` recorded that cost; this module is the answer to it, and
// `sqlite-lifecycle.test.ts` drives the whole state machine with fakes.
//
// **What is here and what is deliberately NOT.** Here: the guards that decide whether a
// half runs at all, the per-page-load state that stops it running twice, the order of
// the `localStorage` writes each outcome produces, the budgets, the grace-period counter
// and the cancellation. Not here: WHEN each of these is called. That is boot ordering,
// it is a documented contract (`design-docs/boot-sequence.md`), and it stays in
// `AppComponent` where the boot sequence is — every call site is one line there.
//
// Also not here: anything that reads a browser global or an Angular service. Those come
// in through {@link SqliteLifecycleDeps}, which `AppComponent` builds, exactly as
// `reload-teardown.ts` is handed `ReloadTeardownDeps`. That is what keeps this file
// loadable under the node test runner.
//
// Everything these methods DECIDE is in `services/sqlite-migration-decisions.ts` and
// `services/local-database-engine-decisions.ts`; everything they DO is in
// `sqlite-migration.ts` and `sqlite-migration-reverse.ts`. This module is the
// orchestration between the two.
//
// See design-docs/android-local-database-engine.md.

import { bucketForIdentifier } from '@cf-shared/payment-provider-bucketing';
import { ConfigKey } from '../services/config-keys';
import { errorReportFields } from '../services/error-patterns';
import {
  SQLITE_MIGRATION_ATTEMPTS_KEY,
  SQLITE_MIGRATION_DONE_KEY,
} from './legacy-idb-cleanup';
import { SUPERSEDED_CLEANUP_ATTEMPTS_KEY } from './superseded-idb-cleanup';
import { PROGRESS_BAR_MAX_WAIT_MS, ScheduleTimeout, waitForAnimationOrTimeout } from './boot-progress-wait';
import {
  LOCAL_DATABASE_ENGINE_KEY,
  LOCAL_DATABASE_ENGINE_VALUE_IDB,
  LOCAL_DATABASE_ENGINE_VALUE_SQLITE,
  LocalDatabaseEngine,
  SQLITE_MIGRATION_ABORTS_KEY,
  SQLITE_MIGRATION_BLOCKED_KEY,
  SQLITE_MIGRATION_COPY_ATTEMPTS_KEY,
  SQLITE_MIGRATION_REQUESTED_KEY,
  SQLITE_MIGRATION_ROLLED_BACK_KEY,
  SQLITE_READY_BOOTS_KEY,
  SQLITE_READY_BOOTS_TEST_OVERRIDE_KEY,
  SQLITE_ROLLBACK_ATTEMPTS_KEY,
  SQLITE_ROLLBACK_BLOCKED_KEY,
  SQLITE_ROLLBACK_COMPLETION_KEY,
  SQLITE_ROLLBACK_REQUESTED_KEY,
  shouldMigrateThisDeviceToSqlite,
  shouldRollBackThisDeviceToIndexedDb,
} from '../services/local-database-engine-decisions';
import {
  MigrationOutcome,
  MigrationPhase,
  ReadyBootCounterReset,
  RollbackOutcome,
  ROLLBACK_PROBLEM_NAMES,
  abortResetsAbortBudget,
  abortSpendsAbortBudget,
  isAbortBudgetExhausted,
  isCopyBudgetExhausted,
  outcomeSpendsCopyBudget,
  mayDeleteSupersededIndexedDbCopy,
  readConfiguredReArmGeneration,
  readMigrationCounter,
  readRolledBackMarker,
  readSqliteReadyBoots,
  resolveReArmGeneration,
  rollbackOutcomeSpendsCopyBudget,
  shouldCountReadyBoot,
  shouldRequestMigration,
  shouldRequestRollback,
  shouldRunMigrationThisBoot,
  shouldRunRollbackThisBoot,
} from '../services/sqlite-migration-decisions';
import {
  MIGRATION_COMPLETED_INFO,
  MIGRATION_PROBLEM_NAMES,
  MigrationReporter,
  MigrationResult,
  SqliteFollowHandle,
  SqliteMigrationDeps,
  runSqliteMigrationAsync,
  startFollowingSourcesAsync,
} from './sqlite-migration';
import {
  RollbackResult,
  SqliteRollbackDeps,
  resolveRollbackTail,
  runSqliteRollbackAsync,
  startFollowingSqliteSourcesAsync,
} from './sqlite-migration-reverse';

/**
 * Everything the lifecycle reaches outside itself.
 *
 * Every entry is either an Angular service, a browser global, or a piece of the boot
 * screen. None of them can exist in a node test, which is the whole reason they are
 * injected rather than imported: a test supplies its own and drives the state machine
 * for real.
 */
export interface SqliteLifecycleDeps {
  /** Sentry and the console, through `LoggingService`. */
  reporter: MigrationReporter;

  /**
   * `localStorage`, or null when the document cannot have any.
   *
   * A function rather than a value because reading the global THROWS when the browser
   * has site data blocked, and that throw has to happen where the old code had it —
   * inside each method's own guard — rather than when this object is built.
   */
  localStorageOrNull(): Storage | null;

  /** Android, running natively. Every half of this is a no-op anywhere else. */
  isAndroidNative(): boolean;

  /** This device's id, for the percentage bucket. */
  deviceId(): string | undefined;

  /**
   * The LATCHING engine read. Safe in the background and deciding halves, which run
   * long after the boot has opened databases, and never used by the switch halves —
   * see the comments there.
   */
  latchedEngine(): LocalDatabaseEngine;

  /** Has anything asked the engine question yet on this page load? */
  engineHasBeenRead(): boolean;

  /** Resolves once `ConfigDbService` has loaded. */
  waitForConfigLoadedAsync(): Promise<unknown>;

  /** One config value, by key. */
  getConfigValue(key: string): unknown;

  /** The boot stage right now, for a report field. */
  describeBootStage(): unknown;

  /** Move the boot screen onto the migration stage. */
  enterMigrationBootStage(): void;

  /** Move the boot screen onto the rollback stage. */
  enterRollbackBootStage(): void;

  /** Glide the boot progress bar to `fraction`. Resolves when the animation settles. */
  animateProgressBarAsync(fraction: number): Promise<unknown>;

  /** `setTimeout`, injected so a test does not wait a real second. */
  scheduleTimeout: ScheduleTimeout;

  /**
   * Everything a copy needs from the outside world, built by `AppComponent` because all
   * of it is browser globals and Angular services. See `buildSqliteMigrationDeps` there.
   */
  buildMigrationDeps(uid: string, storage: Storage, phase: MigrationPhase): SqliteMigrationDeps;

  /** The same, for the way back. See `buildSqliteRollbackDeps` in `AppComponent`. */
  buildRollbackDeps(uid: string, storage: Storage, phase: MigrationPhase): SqliteRollbackDeps;

  /**
   * Delete the IndexedDB generation a rollback abandoned. (#5270 step 6b)
   *
   * A dep rather than a direct call so a node test can drive the orchestration around it
   * — the gate, the once-per-app-run promise — without a real `indexedDB`, which does not
   * exist under node. `AppComponent` supplies the real one.
   */
  cleanUpSupersededIndexedDbAsync(storage: Storage): Promise<void>;

  /**
   * The copying itself. **Production leaves every one of these out and gets the real
   * functions.**
   *
   * They exist as a seam because there is no other way to test what this file is FOR. A
   * copy needs a live PouchDB, a native SQLite bridge and a real IndexedDB, none of
   * which a node test has — so without this, every test could reach only the guards that
   * return before a copy starts, and the part that matters most (which markers a given
   * outcome writes, which budget it spends, when the grace-period counter is reset)
   * would stay untestable. A test supplies a function that simply answers a chosen
   * outcome, and the decisions below then run for real against a fake `Storage`.
   */
  runMigrationAsync?: typeof runSqliteMigrationAsync;
  runRollbackAsync?: typeof runSqliteRollbackAsync;
  resolveRollbackTail?: typeof resolveRollbackTail;
  startFollowingSourcesAsync?: typeof startFollowingSourcesAsync;
  startFollowingSqliteSourcesAsync?: typeof startFollowingSqliteSourcesAsync;
}

/**
 * One device's engine lifecycle for one page load.
 *
 * **The state below is per PAGE LOAD, and that is load-bearing rather than incidental.**
 * It was fields on `AppComponent`, which is created once per page load and never
 * destroyed; this object is created in the same place and has the same lifetime. A
 * longer-lived one would let "already ran this page load" leak across boots and silently
 * disable a half; a shorter-lived one would let two copies race into the same target.
 */
export class SqliteLifecycle {
  /** The real copy functions unless a test supplied its own — see the deps comment. */
  private readonly runMigration: typeof runSqliteMigrationAsync;
  private readonly runRollback: typeof runSqliteRollbackAsync;
  private readonly resolveTail: typeof resolveRollbackTail;
  private readonly followSources: typeof startFollowingSourcesAsync;
  private readonly followSqliteSources: typeof startFollowingSqliteSourcesAsync;

  // Resolved in the constructor BODY rather than as field initialisers, so the order in
  // which TypeScript emits parameter properties and field initialisers cannot matter:
  // `deps` is certainly assigned by the time these read it.
  constructor(private readonly deps: SqliteLifecycleDeps) {
    this.runMigration = deps.runMigrationAsync ?? runSqliteMigrationAsync;
    this.runRollback = deps.runRollbackAsync ?? runSqliteRollbackAsync;
    this.resolveTail = deps.resolveRollbackTail ?? resolveRollbackTail;
    this.followSources = deps.startFollowingSourcesAsync ?? startFollowingSourcesAsync;
    this.followSqliteSources =
      deps.startFollowingSqliteSourcesAsync ?? startFollowingSqliteSourcesAsync;
  }

  /** Guards R1's "once per page load". Not a `localStorage` key — it is per page
   *  load, and the warm-resume failure fallback re-enters `postLoginColdStartAsync`
   *  after databases have already been opened and shut down. */
  private migrationRanThisPageLoad = false;

  /** Guards "once per page load", like the migration's own flag and for the same reason. */
  private rollbackRanThisPageLoad = false;

  /** Guards "one count per page load" for the grace-period counter. */
  private readyBootCounted = false;

  /** Guards "one background copy per page load" — it is started from both boot paths. */
  private backgroundCopyInFlight?: Promise<void>;

  /** Guards "one background reverse copy per page load" — it is started from both paths. */
  private rollbackCopyInFlight?: Promise<void>;

  /**
   * The in-flight run of the SUPERSEDED-generation cleanup, if one has been started.
   *
   * Its own promise rather than the forward cleanup's: they are different passes with
   * different budgets, and a device is never eligible for both at once — one needs the
   * device to be on SQLite, the other needs it on IndexedDB.
   */
  private supersededCleanupInFlight?: Promise<void>;

  /** The live follow keeping the copies current, when one is running. (#5449) */
  private follow?: SqliteFollowHandle;

  /** Set once the app is tearing down, so no further copy slice starts. (#5449) */
  private cancelled = false;

  /** Cancels for the replications running right now, one per in-flight slice. (#5449) */
  private readonly cancellers = new Set<() => void>();

  // ─── the forward direction: IndexedDB → SQLite ────────────────────────────────
  //
  // The migration deliberately spans TWO page loads. One boot DECIDES and writes a
  // marker; the next boot performs the copy. Splitting it that way is what keeps the
  // config read off the boot path of every Android device: the deciding check is a
  // handful of synchronous `localStorage` reads, so a device that is not migrating
  // pays nothing, and only a device already told to migrate opens anything.

  /**
   * The MIGRATING boot. Copies this device's data, then writes the engine marker.
   *
   * **Called from ONE site, immediately above the warm/cold branch in
   * `postLoginAsync`, and reached on BOTH branches.** That position is the
   * requirement, not a convenience:
   *
   *  - It is after `auth.initAsync()`, so there is a signed-in user id — the config
   *    is resolved per user and the database names contain the uid.
   *  - It is before any `DatabaseWrapper` opens, so nothing has yet asked which
   *    engine this device uses. That answer is LATCHED on its first read, so a
   *    migration that wrote the marker afterwards would leave the app spending the
   *    rest of the session reading and writing the old engine while the data sat in
   *    the new one.
   *  - It must NOT move inside `postLoginColdStartAsync`. The warm-resume failure
   *    fallback calls that method again, after databases have been opened and shut
   *    down, where the latch has already been read and the guard below would abort.
   *  - It must NOT be gated to the cold path. Warm resume is a full page-load boot
   *    that synthesises login from `localStorage`, and `setWarmResumeFlag` fires
   *    after every successful cold boot for a paid user — the code itself calls warm
   *    resume "the dominant cold-relaunch path". A cold-only hook would fire roughly
   *    once per account, ever.
   *
   * Never throws: a throw here would be an unhandled rejection in the middle of a
   * boot. Every failure comes back as an outcome, which decides the report and which
   * budget, if any, is spent.
   */
  async runSqliteMigrationIfRequestedAsync(uid: string | undefined): Promise<void> {
    const storage = this.deps.localStorageOrNull();
    if (!storage) return;

    let decided: boolean;
    try {
      decided = shouldRunMigrationThisBoot({
        requestedMarker: storage.getItem(SQLITE_MIGRATION_REQUESTED_KEY),
        blockedMarker: storage.getItem(SQLITE_MIGRATION_BLOCKED_KEY),
        isAndroidNative: this.deps.isAndroidNative(),
        // NOT the latching engine read: that would TAKE the latch, and the guard below
        // exists precisely to check whether the latch has been taken. Asking the
        // question to find out whether the question has been asked would abort the
        // migration on its own footprint, on every device.
        currentEngine: storage.getItem(LOCAL_DATABASE_ENGINE_KEY) === LOCAL_DATABASE_ENGINE_VALUE_SQLITE
          ? LocalDatabaseEngine.Sqlite
          : LocalDatabaseEngine.IndexedDb,
        uid,
        alreadyRanThisPageLoad: this.migrationRanThisPageLoad,
      });
    } catch (e) {
      // a browser with site data blocked throws from the localStorage getter itself.
      // Not migrating is the safe answer, and the engine accessor already reports
      // an unreadable marker at problem tier.
      return;
    }
    if (!decided) return;

    this.migrationRanThisPageLoad = true;

    // R3: if anything has already asked which engine this device uses, the answer is
    // latched and copying now would strand the session on the old engine. That is a
    // WIRING bug rather than a device problem, so it is reported loudly and spends
    // no budget — spending one would permanently disable migration for the device
    // and file it identically to a real failure.
    if (this.deps.engineHasBeenRead()) {
      this.deps.reporter.trackSilentProblem(MIGRATION_PROBLEM_NAMES[MigrationOutcome.LatchAlreadyRead], {
        boot_stage: this.deps.describeBootStage(),
      });
      return;
    }

    this.deps.enterMigrationBootStage();
    // #5448: bounded like every other wait in this stage. Awaiting `Animation.finished`
    // raw is a known way to wedge: a paused animation (hidden tab, minimized PWA) never
    // settles, and a cancelled one rejects. Same helper and same bound as the L1 (#3986)
    // site at the end of the cold boot.
    //
    // #5449 removed the watchdog suppression this comment used to warn about. The stage
    // is now a delta of tens of documents rather than a five-minute copy, so the
    // watchdog watches it like any other boot stage — but every wait here stays bounded,
    // because a bound is what turns a wedged store into a reported outcome rather than a
    // splash that never changes.
    await waitForAnimationOrTimeout(
      this.deps.animateProgressBarAsync(0.15),
      PROGRESS_BAR_MAX_WAIT_MS,
      this.deps.scheduleTimeout,
    );

    // R8: the copy budget is spent HERE, before the first read — not when a failure
    // is classified. A boot whose renderer is killed mid-copy (out of memory on a
    // large database on a low-end phone, which is this population) would otherwise
    // write nothing at all: no counter, no cleared marker, no report, request marker
    // still set. Every launch would restart the same doomed copy for ever.
    const copyAttempts = readMigrationCounter(storage.getItem(SQLITE_MIGRATION_COPY_ATTEMPTS_KEY)) + 1;
    storage.setItem(SQLITE_MIGRATION_COPY_ATTEMPTS_KEY, String(copyAttempts));

    let result: MigrationResult;
    const startedAtMs = Date.now();
    try {
      result = await this.runMigration({
        // `uid!` because `shouldRunMigrationThisBoot` answers false without one, so
        // reaching this line means there is a signed-in user
        ...this.deps.buildMigrationDeps(uid!, storage, MigrationPhase.Switch),
        onProgress: (done, total) =>
          void this.deps.animateProgressBarAsync(0.15 + 0.7 * (done / Math.max(total, 1))),
      });
    } catch (e) {
      // runSqliteMigrationAsync is documented never to throw; this is the belt for
      // a bug in that promise, and it must not take the boot down with it.
      this.deps.reporter.trackSilentProblem('AndroidSqliteMigrationThrew', errorReportFields(e));
      return;
    }

    this.applyMigrationOutcome(storage, result, copyAttempts, startedAtMs);
  }

  /**
   * The BACKGROUND half: copy this device's data while the app carries on running.
   * (#5449)
   *
   * Fire-and-forget, from both boot paths, and never awaited by anything. It is the
   * slow part of the migration — about five minutes on a real account — and #5449 is
   * the ticket that moved it off the boot screen, so nothing here may hold a boot.
   *
   * It writes no engine marker and takes no budget decisions. All it does is get the
   * data across and verify it; the switch half, on a later page load, is what turns
   * that into a migrated device.
   *
   * When the copy is done and verified it starts FOLLOWING the source, so that
   * everything written from then on — by the user, or by an ordinary sync from the
   * server — reaches SQLite as it happens rather than waiting for the switch.
   *
   * Never throws: nothing is waiting for it, so a throw here would surface as an
   * unhandled rejection in the middle of a running app.
   */
  async runSqliteBackgroundCopyAsync(uid: string | undefined): Promise<void> {
    // one per page load. Cold start and warm resume can both reach this, and two copies
    // racing into the same SQLite target would fight over one native bridge.
    this.backgroundCopyInFlight ??= this.startBackgroundCopyAsync(uid);
    await this.backgroundCopyInFlight;
  }

  /**
   * The background copy itself: the eligibility guards, the run, and the follow it
   * starts afterwards. (#5449)
   *
   * Separate from `runSqliteBackgroundCopyAsync` above, which does nothing but keep two
   * boot paths from starting two copies into the same SQLite targets. Never throws, for
   * the reason that wrapper gives: nothing awaits this.
   */
  private async startBackgroundCopyAsync(uid: string | undefined): Promise<void> {
    try {
      // only a device that could actually migrate pays anything for this
      if (!this.deps.isAndroidNative()) return;
      if (this.deps.latchedEngine() !== LocalDatabaseEngine.IndexedDb) return;
      if (!uid) return;

      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      // the same two markers the switch half honours: a device told to stop, and a
      // device that has never been told to start
      if (!storage.getItem(SQLITE_MIGRATION_REQUESTED_KEY)) return;
      if (storage.getItem(SQLITE_MIGRATION_BLOCKED_KEY)) return;

      const result = await this.runMigration(
        this.deps.buildMigrationDeps(uid, storage, MigrationPhase.Background));

      if (result.outcome !== MigrationOutcome.Completed) {
        // the run reports its own failures under their own names; there is nothing for
        // this half to decide, because it writes no marker and spends no budget
        return;
      }

      console.log(`SQLite migration (phase 3): background copy of `
        + `${result.databasesCopied} databases done; following for later changes`);

      // Keep the copies current until this page load ends. Without this, everything
      // written between here and the switch would be waiting for the switch to carry
      // it — which is the delta this change exists to keep small.
      // the copy can finish in the same moment a teardown begins, and a follow started
      // after that would replicate into files the wipe has just deleted
      if (this.cancelled) return;

      this.follow = await this.followSources(
        this.deps.buildMigrationDeps(uid, storage, MigrationPhase.Background),
        result.copiedDatabaseNames ?? []);
    } catch (e) {
      // background housekeeping nobody should be interrupted for, but it must not
      // vanish either — a device that never manages to COPY is invisible otherwise
      this.deps.reporter.trackSilentProblem('AndroidSqliteMigrationBackgroundFailed',
        errorReportFields(e));
    }
  }

  /**
   * Turn one migration outcome into the marker writes, the budgets and the report.
   *
   * Separated from the run above so the ORDER of these writes is readable in one
   * place: the engine marker moves only on `Completed`, and only then is the request
   * marker cleared.
   */
  private applyMigrationOutcome(
    storage: Storage,
    result: MigrationResult,
    copyAttempts: number,
    startedAtMs: number,
  ): void {
    const { outcome } = result;

    if (outcome === MigrationOutcome.Completed) {
      // R7: the marker moves ONCE, at the end, after every database was copied AND
      // verified. Never per database — a device that copied three of its four and
      // then died must come back as an IndexedDB device, because that is where all
      // four still are.
      storage.setItem(LOCAL_DATABASE_ENGINE_KEY, LOCAL_DATABASE_ENGINE_VALUE_SQLITE);
      storage.removeItem(SQLITE_MIGRATION_REQUESTED_KEY);
      storage.removeItem(SQLITE_MIGRATION_COPY_ATTEMPTS_KEY);
      storage.removeItem(SQLITE_MIGRATION_ABORTS_KEY);
      // #5270: this is a NEW SQLite copy and it has served no grace period. A device that
      // migrated, served its ten boots, rolled back and migrated again would otherwise
      // arrive here with the counter already satisfied, and delete the fresh IndexedDB copy
      // the rollback had just written — with no grace period at all for the copy it has.
      this.resetReadyBootCount(ReadyBootCounterReset.ReMigrated);
      this.reArmLegacyIdbCleanup(storage);
      this.deps.reporter.trackSilentInfo(MIGRATION_COMPLETED_INFO, {
        databases: result.databasesCopied,
        documents: result.documentsCopied,
        duration_ms: Date.now() - startedAtMs,
        attempt: copyAttempts,
      });
      console.log(`SQLite migration (phase 3): migrated ${result.databasesCopied} databases`);
      // deliberately NO page reload — the reasons are the latch and the already-open
      // handles. This boot opens none before here; a database opened by something else
      // WHILE the copy ran is the `LatchTakenDuringCopy` branch below, and reaching
      // this line means that did not happen (#5448)
      return;
    }

    // #5448: the copy itself worked, but something asked the engine question while it
    // was running, so this page load is committed to IndexedDB. The marker must NOT
    // move — it would send the next boot to a store missing everything this session
    // does from here on. The attempt is handed back by the refund below, and a later
    // boot, one where nothing has asked yet, copies again.
    //
    // Reported under its own name because phase 4's ramp has to tell this apart from a
    // device that is simply slow: if it turns out to be common, the repair is to stop
    // whatever opens a database mid-copy, not to widen any bound.
    if (outcome === MigrationOutcome.LatchTakenDuringCopy) {
      const latchProblemName = MIGRATION_PROBLEM_NAMES[outcome];
      if (latchProblemName) {
        this.deps.reporter.trackSilentProblem(latchProblemName, {
          databases: result.databasesCopied,
          documents: result.documentsCopied,
          duration_ms: Date.now() - startedAtMs,
          attempt: copyAttempts,
        });
      }
    }

    // An abort that ended before any copying began gives the copy budget back: nothing
    // was attempted, so it must not count against the small number of real copy tries.
    // The three genuine copy failures KEEP the attempt — `outcomeSpendsCopyBudget` says
    // which is which, and its comment says why that question cannot be answered with
    // `abortResetsAbortBudget`.
    if (!outcomeSpendsCopyBudget(outcome)) {
      storage.setItem(SQLITE_MIGRATION_COPY_ATTEMPTS_KEY, String(copyAttempts - 1));
    }

    if (abortResetsAbortBudget(outcome)) {
      storage.removeItem(SQLITE_MIGRATION_ABORTS_KEY);
    }

    let aborts = readMigrationCounter(storage.getItem(SQLITE_MIGRATION_ABORTS_KEY));
    if (abortSpendsAbortBudget(outcome)) {
      aborts += 1;
      storage.setItem(SQLITE_MIGRATION_ABORTS_KEY, String(aborts));
    }

    // "the config now says no" is the kill switch working, not a failure — clear the
    // request and say nothing
    if (outcome === MigrationOutcome.ConfigSaysNo) {
      storage.removeItem(SQLITE_MIGRATION_REQUESTED_KEY);
      return;
    }

    // #5449: the background half has not finished copying yet, which is the ordinary
    // state of every migrating device until it has. **The request marker must SURVIVE
    // this**, and that is the whole reason for this early return.
    //
    // The background half's first check is that same marker, and it runs later in the
    // SAME page load. Falling through to the unconditional `removeItem` below would
    // clear it before the background copy ever looked, so the copy would return
    // immediately — on this boot, and on every boot afterwards, because the deciding
    // check writes the request back and this half clears it again. Nothing would ever
    // be copied, the switch would never find a verified copy to finish, and none of it
    // would be reported: a silent loop that leaves the feature doing nothing at all.
    if (outcome === MigrationOutcome.BulkCopyNotReady) return;

    // the run itself already reported a per-database copy failure under its own
    // name; only the aborts it could not report are reported here
    const problemName = MIGRATION_PROBLEM_NAMES[outcome];
    if (problemName && result.databasesCopied === 0 && abortSpendsAbortBudget(outcome)) {
      this.deps.reporter.trackSilentProblem(problemName, {
        consecutive_aborts: aborts,
        // the underlying reason, not just the outcome name — this abort counts
        // towards giving up on the device permanently
        ...errorReportFields(result.error),
      });
    }

    storage.removeItem(SQLITE_MIGRATION_REQUESTED_KEY);

    // R8: exhaustion writes its OWN marker. Clearing the request alone is not enough
    // — the deciding check runs later in this SAME page load, sees an Android device
    // still on IndexedDB with the controls saying yes, and writes it straight back.
    // The two exhaustions report under DIFFERENT names: "the store never answers" and
    // "the copy keeps failing" are different repairs, and phase 4's ramp counts them
    // separately.
    if (isAbortBudgetExhausted(aborts)) {
      storage.setItem(SQLITE_MIGRATION_BLOCKED_KEY, 'aborts');
      this.deps.reporter.trackSilentProblem('AndroidSqliteMigrationAbortsExhausted',
        { aborts, last_outcome: outcome });
      return;
    }
    if (isCopyBudgetExhausted(readMigrationCounter(storage.getItem(SQLITE_MIGRATION_COPY_ATTEMPTS_KEY)))) {
      storage.setItem(SQLITE_MIGRATION_BLOCKED_KEY, 'copy-budget');
      this.deps.reporter.trackSilentProblem('AndroidSqliteMigrationCopyBudgetExhausted',
        { attempts: copyAttempts, last_outcome: outcome });
    }
  }

  /**
   * The DECIDING boot. Asks whether this device should migrate on its NEXT page load,
   * and writes the request marker if so. Copies nothing.
   *
   * Runs after the warm/cold branch because it needs `ConfigDbService` to have
   * loaded, which each branch starts. Fire-and-forget and never throws: nothing in
   * the boot waits on it, and a device that does not migrate must pay nothing for
   * this.
   */
  async decideSqliteMigrationAsync(uid: string | undefined): Promise<void> {
    try {
      // only a device that could actually migrate pays for the config wait
      if (!this.deps.isAndroidNative()) return;
      if (this.deps.latchedEngine() !== LocalDatabaseEngine.IndexedDb) return;

      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      // already asked for: nothing to do, and rewriting it would be a needless write
      if (storage.getItem(SQLITE_MIGRATION_REQUESTED_KEY)) return;

      await this.deps.waitForConfigLoadedAsync();

      const requested = shouldRequestMigration({
        isAndroidNative: true,
        currentEngine: LocalDatabaseEngine.IndexedDb,
        uid,
        blockedMarker: storage.getItem(SQLITE_MIGRATION_BLOCKED_KEY),
        rolledBackMarker: storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY),
        // #5270: the re-arm generation in force, which is the ONLY thing that can lift a
        // rollback's hold. Unreadable or missing resolves to the floor, which keeps every
        // rolled-back device held — the opposite direction from the two dials below, and
        // getting it backwards re-creates the migrate-roll-back-migrate lap.
        reArmGeneration: resolveReArmGeneration(
          this.deps.getConfigValue(ConfigKey.AndroidSqliteMigrationRearmGeneration)),
        controlsSayMigrate: shouldMigrateThisDeviceToSqlite(
          this.deps.getConfigValue(ConfigKey.AndroidSqliteMigrationAllowList),
          this.deps.getConfigValue(ConfigKey.AndroidSqliteMigrationPercent),
          uid,
          this.deps.deviceId(),
          bucketForIdentifier,
        ),
      });
      if (!requested) return;

      storage.setItem(SQLITE_MIGRATION_REQUESTED_KEY, 'true');
      console.log('SQLite migration (phase 3): requested; the next page load will copy');
    } catch (e) {
      // background housekeeping nobody should be interrupted for, but it must not
      // vanish either — a device that never manages to REQUEST is invisible otherwise
      this.deps.reporter.trackSilentProblem('AndroidSqliteMigrationDecideFailed', errorReportFields(e));
    }
  }

  // ─── #4981 phase 4 (#5270): the way BACK ────────────────────────────────────
  //
  // The mirror image of the three migration hooks above — a deciding session, a
  // background copy, and a switch half at the top of a later page load — plus one thing
  // the forward direction has no equivalent of: the TAIL, which finishes a rollback whose
  // commit point has already been passed.

  /**
   * Count one successful boot on SQLite towards the grace period. (#5270)
   *
   * The grace period is the delay before a device deletes an IndexedDB copy it no longer
   * uses, and what it is really measuring is "this device has run on the engine it is on
   * for a while and nothing went wrong". Only a boot that reached `Ready` counts.
   *
   * **It counts on WHICHEVER ENGINE the device is using (#5270 step 6b).** On SQLite it
   * guards the pre-migration IndexedDB copy. On a device that has ROLLED BACK — which is
   * on IndexedDB again, carrying superseded names in the rolled-back marker — it guards
   * the generation that device abandoned. One counter is safe for both because it is reset
   * on every engine change, so the two meanings can never mix in one stored number; a
   * device that never migrated has neither and counts nothing.
   *
   * Never throws: it is called from the `bootStage` setter, on the boot path.
   */
  countSqliteReadyBoot(): void {
    try {
      const storage = this.deps.localStorageOrNull();
      if (!storage) return;

      if (!shouldCountReadyBoot({
        // the RAW marker, not the latching read. By this point in the boot the latch has
        // certainly been taken, so either would answer the same — but reading the marker
        // keeps this free of any opinion about latch state, which is what every other
        // phase-3/4 site here does deliberately.
        currentEngine: storage.getItem(LOCAL_DATABASE_ENGINE_KEY) === LOCAL_DATABASE_ENGINE_VALUE_SQLITE
          ? LocalDatabaseEngine.Sqlite
          : LocalDatabaseEngine.IndexedDb,
        // #5270 step 6b: what tells a ROLLED-BACK device — which is back on IndexedDB and
        // has an abandoned generation to delete — apart from one that simply never
        // migrated, which has nothing to count and must not write the key at all.
        hasSupersededIndexedDbNames: (readRolledBackMarker(
          storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY),
        )?.supersededIndexedDbNames.length ?? 0) > 0,
        alreadyCountedThisPageLoad: this.readyBootCounted,
      })) {
        return;
      }

      this.readyBootCounted = true;
      const counted = readMigrationCounter(storage.getItem(SQLITE_READY_BOOTS_KEY)) + 1;
      storage.setItem(SQLITE_READY_BOOTS_KEY, String(counted));
    } catch (e) {
      // A browser with site data blocked throws from the setter. Not counting simply
      // delays a deletion, which is the safe direction, and this runs on the boot path
      // where nothing may be allowed to travel — but a device that can never count is a
      // device whose cleanup silently never happens, so it is worth knowing about.
      this.deps.reporter.trackSilentProblem('AndroidSqliteReadyBootCountFailed', errorReportFields(e));
    }
  }

  /**
   * Put the grace period back to the beginning. (#5270)
   *
   * **The two callers are what stop a sequence that deletes live data.** Without them: a
   * device migrates, serves the whole grace period, rolls back — which writes a FRESH
   * IndexedDB copy — and re-migrates, at which point an unreset counter permits the
   * cleanup to delete that fresh copy immediately, having served no grace period for the
   * copy it actually has.
   *
   * A completed sign-out clears the same key, but from `AuthService.signOutAsync` rather
   * than through here: a different module, which cannot reach a private method.
   *
   * Never throws: every caller is on a path that must reach its end.
   */
  private resetReadyBootCount(reason: ReadyBootCounterReset): void {
    try {
      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      storage.removeItem(SQLITE_READY_BOOTS_KEY);
      this.readyBootCounted = false;
      console.log(`SQLite grace period (phase 4): counter reset (${reason})`);
    } catch (e) {
      // The counter surviving when it should have been reset is the direction that costs
      // data, so this is reported rather than absorbed.
      this.deps.reporter.trackSilentProblem('AndroidSqliteReadyBootResetFailed', {
        reason,
        ...errorReportFields(e),
      });
    }
  }

  /**
   * Put this device back to where a freshly rolled-back device starts. (#5270 step 6b)
   *
   * **Two things, and they are here together because they answer one event** — "this
   * device has finished rolling back" — which happens at TWO call sites: the rollback's
   * own completion branch, and the resumed tail on a later boot. Doing them in one place
   * is what stops the second site from quietly getting only half of it, which is exactly
   * the bug this helper was extracted to fix.
   *
   *  - The grace-period counter goes back to zero. The SQLite copy it was counting for is
   *    no longer the one in use, and the fresh IndexedDB generation has served nothing.
   *  - The superseded cleanup's attempt budget is cleared, so the generation this rollback
   *    just abandoned gets a full budget rather than inheriting a spent one from an earlier
   *    rollback that gave up. This is the same re-arm `reArmLegacyIdbCleanup` does for the
   *    forward pair, for the reason #4834 gives: a spent budget makes a re-arm worthless.
   *
   * Never throws: both callers are on paths that must reach their end.
   */
  private noteRollbackCompleted(): void {
    this.resetReadyBootCount(ReadyBootCounterReset.RolledBack);
    try {
      this.deps.localStorageOrNull()?.removeItem(SUPERSEDED_CLEANUP_ATTEMPTS_KEY);
    } catch (e) {
      // A spent budget surviving costs a duplicate left on disk, never data. Reported
      // rather than absorbed, because nothing else would ever say so.
      this.deps.reporter.trackSilentProblem(
        'AndroidSqliteSupersededBudgetResetFailed', errorReportFields(e));
    }
  }

  /**
   * Give the forward legacy-IndexedDB cleanup a fresh run after a RE-MIGRATION. (#5270
   * step 6b)
   *
   * That cleanup is one-shot per device: `SQLITE_MIGRATION_DONE_KEY` makes every later
   * boot return immediately, and it is written both on success and on giving up after the
   * attempt budget. So without this, a device that migrated, rolled back and migrated
   * again could never delete the SECOND stale IndexedDB copy it is now carrying — the one
   * the rollback wrote and the re-migration abandoned.
   *
   * **The attempts key goes too, and that is not tidiness.** #4834's comment on that key
   * says why: a device inheriting a spent budget gives up after a single try, which makes
   * the re-arm worthless on precisely the devices it exists for.
   *
   * R3 recommended "a versioned done-marker key" for this. A version is a constant and
   * cannot express "once per re-migration", so the keys are cleared at the event instead.
   *
   * iOS never reaches this: the whole migration path refuses a device that is not Android
   * native.
   *
   * Never throws: it runs on a path that must reach its end. The storage is passed in
   * because the caller already holds one and this must not re-read it — a second read
   * could answer differently on a browser that has just started refusing.
   */
  private reArmLegacyIdbCleanup(storage: Storage): void {
    try {
      storage.removeItem(SQLITE_MIGRATION_DONE_KEY);
      storage.removeItem(SQLITE_MIGRATION_ATTEMPTS_KEY);
    } catch (e) {
      // Not re-arming only costs disk space on this device — the stale copy stays.
      // Reported rather than absorbed, because it is invisible otherwise.
      this.deps.reporter.trackSilentProblem(
        'AndroidSqliteCleanupReArmFailed', errorReportFields(e));
    }
  }

  /**
   * Delete the IndexedDB generation this device abandoned when it ROLLED BACK. (#5270
   * step 6b)
   *
   * **Read `superseded-idb-cleanup.ts`'s header before changing anything here.** This runs
   * on a device that is ON INDEXEDDB, whose live databases share the `_pouch_` prefix with
   * the ones being deleted — the opposite of the forward cleanup on `AppComponent`, and
   * the reason the two are separate modules.
   *
   * The gate is the pure `mayDeleteSupersededIndexedDbCopy`, which checks the platform,
   * the engine, whether anything is recorded, the grace period and the operator's switch.
   * That switch is the SAME one the forward cleanup uses: the rollback runbook says to
   * turn it off at the first sign of missing data, and it has to stop both deletions.
   *
   * One run per app run, like the forward cleanup: each run spends one of a bounded
   * budget, and a single app run reaches this from both the cold and the warm path.
   *
   * Never throws, so both call sites can fire it and forget it.
   */
  async cleanUpSupersededIndexedDbAsync(): Promise<void> {
    let storage: Storage;
    let permitted: boolean;
    // **The whole gate is inside the try, and `localStorageOrNull` is not what makes it
    // safe.** That helper is a bare `typeof` check — evaluating `localStorage` is itself
    // what throws on a browser with site data blocked, so the throw happens INSIDE it and
    // travels straight out. Both call sites fire this with `void`, so an escaping throw is
    // an unhandled rejection in the middle of a boot. Every sibling on this path wraps
    // exactly these reads for the same reason.
    try {
      const readStorage = this.deps.localStorageOrNull();
      if (!readStorage) return;
      storage = readStorage;

      // the RAW marker, not the latching read, for the reason the boot counter gives: it
      // keeps this free of any opinion about whether the latch has been taken.
      const currentEngine =
        storage.getItem(LOCAL_DATABASE_ENGINE_KEY) === LOCAL_DATABASE_ENGINE_VALUE_SQLITE
          ? LocalDatabaseEngine.Sqlite
          : LocalDatabaseEngine.IndexedDb;
      const supersededNameCount = readRolledBackMarker(
        storage.getItem(SQLITE_MIGRATION_ROLLED_BACK_KEY),
      )?.supersededIndexedDbNames.length ?? 0;

      permitted = mayDeleteSupersededIndexedDbCopy({
        isAndroidNative: this.deps.isAndroidNative(),
        currentEngine,
        supersededNameCount,
        readyBoots: readSqliteReadyBoots(
          storage.getItem(SQLITE_READY_BOOTS_KEY),
          storage.getItem(SQLITE_READY_BOOTS_TEST_OVERRIDE_KEY),
        ),
        cleanupEnabledValue: this.deps.getConfigValue(ConfigKey.AndroidSqliteCleanupEnabled),
      });
    } catch (e) {
      // Not deleting is the safe direction: it costs disk space, while deleting on a
      // half-read gate could cost the user's last correct copy.
      this.deps.reporter.trackSilentProblem(
        'AndroidSqliteSupersededCleanupGateFailed', errorReportFields(e));
      return;
    }

    if (!permitted) return;

    this.supersededCleanupInFlight ??= this.deps.cleanUpSupersededIndexedDbAsync(storage);
    await this.supersededCleanupInFlight;
  }

  /**
   * Finish a rollback whose commit point has been passed, if there is one.
   *
   * **Called at the very top of the post-login path, BEFORE the rollback and migration
   * hooks and before the warm/cold branch.** That position is the requirement: it is the
   * one window that is both after the engine question can be asked and before
   * `setLocalDatabaseId` reads the #4401 generation, so it is the only point at which the
   * tail can still change either of the two things it has to change.
   *
   * Synchronous, and deliberately so — everything that decides what this device reads is a
   * `localStorage` write, and doing them with no await in between keeps the window in
   * which a page load could be killed as small as the platform allows. Never throws: a
   * throw here would be an unhandled rejection in the middle of a boot.
   */
  resolveSqliteRollbackTailIfAny(uid: string | undefined): void {
    try {
      // the same guard every other half has. Before the lifecycle moved out of the
      // component these methods took the signed-in user and reached `user.uid` through
      // the deps builder, so a missing one could not get this far; taking a `uid`
      // directly means each half checks it for itself.
      if (!uid) return;
      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      // nothing outstanding is the ordinary state of every boot on every device, and this
      // is one synchronous read to find that out
      if (!storage.getItem(SQLITE_ROLLBACK_COMPLETION_KEY)) return;

      // Read the engine BEFORE the tail runs, so the check below can tell "the flip
      // happened on THIS boot" from "it happened on an earlier one". See below for why
      // that distinction decides whether a device ever deletes its duplicate.
      const engineBefore = storage.getItem(LOCAL_DATABASE_ENGINE_KEY);

      // The tail reports its own failures under their own names, and on anything but
      // `Completed` the record deliberately stays put so the next boot picks up where this
      // one stopped. There is nothing for this method to decide either way, which is why
      // the answer is not branched on.
      this.resolveTail(this.deps.buildRollbackDeps(uid, storage, MigrationPhase.Switch));

      // **The tail is the OTHER place the engine flips, and it must reset what the
      // rollback's own completion branch resets.** When the tail finishes on the page load
      // that committed, `applyRollbackOutcome` sees `Completed` and does that. When it does
      // NOT — the page load is killed, or the tail answers `TailWriteFailed`,
      // `GenerationBumpMismatch` or `GenerationOutOfRange` — a later boot finishes it here
      // instead, and by then `runSqliteRollbackIfRequestedAsync` returns at its engine
      // guard, so that branch is never reached.
      //
      // Leaving the counter alone there deletes user data. The device arrives on IndexedDB
      // carrying the count it accrued during its SQLite life, which for anything an
      // operator rolls back after normal use is far past
      // `REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP` — so the superseded cleanup's gate opens on
      // the FIRST post-rollback boot and deletes the pre-rollback generation with no grace
      // period at all. That generation is the only correct copy left if the reverse copy
      // carried something wrong out of SQLite, which is the whole reason the period exists.
      //
      // **Only on the boot the flip actually happens — "the engine is now idb" is NOT the
      // same question, and using it resets the counter for ever.** The tail is re-run on
      // every boot while the completion record is still there, and that record is kept
      // whenever any SQLite delete failed (`deleteSupersededSqliteDatabasesAsync` returns
      // early unless all of them succeeded). On such a device the tail reaches its
      // `setEngineMarker(idb)` again every boot, harmlessly — but a reset there would put
      // the grace-period counter back to zero every boot too, so it could never reach
      // `REQUIRED_SQLITE_BOOTS_BEFORE_CLEANUP` and the superseded generation would be kept
      // for ever. That is the exact duplicate this whole step exists to remove, arriving
      // from the opposite direction.
      //
      // Read the marker back rather than trusting the call: the tail deliberately does
      // nothing on several outcomes and leaves the record in place for the next boot.
      const flippedOnThisBoot = engineBefore !== LOCAL_DATABASE_ENGINE_VALUE_IDB
        && storage.getItem(LOCAL_DATABASE_ENGINE_KEY) === LOCAL_DATABASE_ENGINE_VALUE_IDB;
      if (flippedOnThisBoot) {
        this.noteRollbackCompleted();
      }
    } catch (e) {
      this.deps.reporter.trackSilentProblem('AndroidSqliteRollbackTailThrew', errorReportFields(e));
    }
  }

  /**
   * The ROLLING-BACK boot: the switch half. Finishes the copy the background half made,
   * verifies what is outstanding, and commits.
   *
   * The same single call site as the migration's switch half, immediately above the
   * warm/cold branch, and for the same two reasons: there is a signed-in user id by here,
   * and nothing has yet asked which engine this device uses.
   *
   * The two can never both act on one page load — migrating requires the device to be on
   * IndexedDB and rolling back requires it to be on SQLite — so the order between them is
   * a matter of reading, not of correctness. The tail runs before both, because it is what
   * makes the engine answer current.
   *
   * Never throws.
   */
  async runSqliteRollbackIfRequestedAsync(uid: string | undefined): Promise<void> {
    const storage = this.deps.localStorageOrNull();
    if (!storage) return;

    try {
      const decided = shouldRunRollbackThisBoot({
        // The engine is read from the RAW marker, never through the latching accessor:
        // that would take the latch, and the guard below exists precisely to check
        // whether the latch has been taken. Asking the question to find out whether the
        // question has been asked would abort the rollback on its own footprint, on
        // every device.
        currentEngine: storage.getItem(LOCAL_DATABASE_ENGINE_KEY) === LOCAL_DATABASE_ENGINE_VALUE_SQLITE
          ? LocalDatabaseEngine.Sqlite
          : LocalDatabaseEngine.IndexedDb,
        alreadyRanThisPageLoad: this.rollbackRanThisPageLoad,
        blockedMarker: storage.getItem(SQLITE_ROLLBACK_BLOCKED_KEY),
        requestedMarker: storage.getItem(SQLITE_ROLLBACK_REQUESTED_KEY),
        isAndroidNative: this.deps.isAndroidNative(),
        uid,
      });
      if (!decided) return;
    } catch {
      // a browser with site data blocked throws from the localStorage GETTER, before
      // `getItem` is reached. Not rolling back is the correct answer there — the rollback
      // has to persist a completion record to be safe at all — and the condition is
      // ALREADY reported on this same page load: the engine accessor raises
      // LocalDatabaseEngineMarkerUnreadable at problem tier for exactly this browser
      // state. Reporting it again here would duplicate that, not add to it.
      return;
    }

    this.rollbackRanThisPageLoad = true;

    // If anything has already asked which engine this device uses, the answer is latched
    // and committing now would strand the session on SQLite while the next boot came up on
    // IndexedDB. A WIRING bug rather than a device problem, so it is reported loudly and
    // spends no budget.
    if (this.deps.engineHasBeenRead()) {
      this.deps.reporter.trackSilentProblem(
        ROLLBACK_PROBLEM_NAMES[RollbackOutcome.LatchAlreadyRead]!,
        { boot_stage: this.deps.describeBootStage() });
      return;
    }

    this.deps.enterRollbackBootStage();
    // Bounded, like every other wait in this stage. Awaiting `Animation.finished` raw is a
    // known way to wedge: a paused animation (hidden tab, minimized PWA) never settles, and
    // a cancelled one rejects.
    await waitForAnimationOrTimeout(
      this.deps.animateProgressBarAsync(0.15),
      PROGRESS_BAR_MAX_WAIT_MS,
      this.deps.scheduleTimeout,
    );

    // The budget is spent HERE, before the first read — not when a failure is classified.
    // A boot whose renderer is killed mid-copy would otherwise write nothing at all, and
    // every launch would restart the same doomed copy for ever.
    const attempts = readMigrationCounter(storage.getItem(SQLITE_ROLLBACK_ATTEMPTS_KEY)) + 1;
    storage.setItem(SQLITE_ROLLBACK_ATTEMPTS_KEY, String(attempts));

    let result: RollbackResult;
    const startedAtMs = Date.now();
    try {
      result = await this.runRollback({
        ...this.deps.buildRollbackDeps(uid, storage, MigrationPhase.Switch),
        onProgress: (done, total) =>
          void this.deps.animateProgressBarAsync(0.15 + 0.7 * (done / Math.max(total, 1))),
      });
    } catch (e) {
      // runSqliteRollbackAsync is documented never to throw; this is the belt for a bug in
      // that promise, and it must not take the boot down with it.
      this.deps.reporter.trackSilentProblem('AndroidSqliteRollbackThrew', errorReportFields(e));
      return;
    }

    this.applyRollbackOutcome(storage, result, attempts, startedAtMs);
  }

  /**
   * Turn one rollback outcome into the marker writes, the budget and the report.
   *
   * Separated from the run above so the ORDER of these writes is readable in one place.
   * Note what is NOT here: the engine marker. The rollback's commit point and its tail own
   * that, because moving the engine and moving the #4401 generation have to happen together
   * or not at all — which is the whole reason the completion record exists.
   */
  private applyRollbackOutcome(
    storage: Storage,
    result: RollbackResult,
    attempts: number,
    startedAtMs: number,
  ): void {
    const { outcome } = result;

    // Hand the attempt back whenever no copying actually happened. The budget is spent
    // before the first read, so a page load killed mid-copy still counts — and that is only
    // fair if everything which ended BEFORE the copy gives its attempt back. The case that
    // makes this load-bearing rather than tidy: a device whose background copy is still
    // running answers `BulkCopyNotReady` on EVERY page load, so without the refund three
    // ordinary launches would exhaust a budget of 3 and block the device from ever rolling
    // back — on the one control an operator reaches for during an incident.
    if (!rollbackOutcomeSpendsCopyBudget(outcome)) {
      storage.setItem(SQLITE_ROLLBACK_ATTEMPTS_KEY, String(attempts - 1));
    }

    if (outcome === RollbackOutcome.Completed) {
      storage.removeItem(SQLITE_ROLLBACK_REQUESTED_KEY);
      storage.removeItem(SQLITE_ROLLBACK_ATTEMPTS_KEY);
      // the SQLite copy this device spent a grace period on is no longer the one in use
      this.noteRollbackCompleted();
      console.log(`SQLite rollback (phase 4): rolled back ${result.databasesCopied} `
        + `databases in ${Date.now() - startedAtMs}ms on attempt ${attempts}`);
      // deliberately NO page reload, for the same reasons the forward switch gives: the
      // latch is unread on this page load, and everything the device reads was decided
      // above, before any database was opened
      return;
    }

    // The ordinary states, which report nothing and withdraw nothing. `BulkCopyNotReady` is
    // a device part-way through a copy that legitimately takes several sessions, and
    // `Cancelled` is the app tearing down.
    if (outcome === RollbackOutcome.BulkCopyNotReady || outcome === RollbackOutcome.Cancelled) {
      return;
    }

    // The kill switch working: an operator took this person back off the rollback list. The
    // request is WITHDRAWN rather than merely ignored, because leaving it set would make
    // every later page load re-open the common database to be told "no" again.
    if (outcome === RollbackOutcome.ConfigSaysNo) {
      storage.removeItem(SQLITE_ROLLBACK_REQUESTED_KEY);
      storage.removeItem(SQLITE_ROLLBACK_ATTEMPTS_KEY);
      return;
    }

    // Something went wrong. A device that has spent every attempt stays on SQLite with all
    // of its data — which is where it already was — and stops trying, so a phone that
    // cannot copy itself back does not spend every launch failing to. The budget is only
    // reached by outcomes where the copy was really tried, per the refund above.
    if (isCopyBudgetExhausted(attempts) && rollbackOutcomeSpendsCopyBudget(outcome)) {
      storage.setItem(SQLITE_ROLLBACK_BLOCKED_KEY, 'true');
      storage.removeItem(SQLITE_ROLLBACK_REQUESTED_KEY);
      this.deps.reporter.trackSilentProblem('AndroidSqliteRollbackGivingUp', {
        outcome,
        attempts,
        ...errorReportFields(result.error),
      });
    }
  }

  /**
   * The BACKGROUND half of the way back: copy this device's data out of SQLite while the
   * app carries on running on it.
   *
   * Fire-and-forget, from both boot paths, and never awaited by anything — it is the slow
   * part, measured in minutes, and nothing here may hold a boot. It changes nothing the
   * device reads: the copy goes into a #4401 generation nothing has been told to use yet.
   *
   * Never throws: nothing is waiting for it.
   */
  async runSqliteRollbackBackgroundCopyAsync(uid: string | undefined): Promise<void> {
    // one per page load. Cold start and warm resume can both reach this, and two copies
    // racing into the same IndexedDB target would fight over one browser connection.
    this.rollbackCopyInFlight ??= this.startRollbackBackgroundCopyAsync(uid);
    await this.rollbackCopyInFlight;
  }

  /** The background reverse copy itself: the guards, the run, and the follow. */
  private async startRollbackBackgroundCopyAsync(uid: string | undefined): Promise<void> {
    try {
      // only a device that could actually roll back pays anything for this
      if (!this.deps.isAndroidNative()) return;
      if (this.deps.latchedEngine() !== LocalDatabaseEngine.Sqlite) return;
      if (!uid) return;

      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      // the same two markers the switch half honours: a device told to stop, and a device
      // that has never been told to start
      if (!storage.getItem(SQLITE_ROLLBACK_REQUESTED_KEY)) return;
      if (storage.getItem(SQLITE_ROLLBACK_BLOCKED_KEY)) return;

      const result = await this.runRollback(
        this.deps.buildRollbackDeps(uid, storage, MigrationPhase.Background));

      if (result.outcome !== RollbackOutcome.Completed) {
        // the run reports its own failures under their own names; there is nothing for
        // this half to decide, because it commits nothing and spends no budget
        return;
      }

      console.log(`SQLite rollback (phase 4): background copy of `
        + `${result.databasesCopied} databases done; following for later changes`);

      // the copy can finish in the same moment a teardown begins, and a follow started
      // after that would replicate into databases the wipe has just deleted
      if (this.cancelled) return;

      this.follow = await this.followSqliteSources(
        this.deps.buildRollbackDeps(uid, storage, MigrationPhase.Background),
        result.copiedDatabaseNames ?? []);
    } catch (e) {
      // background housekeeping nobody should be interrupted for, but it must not vanish
      // either — a device that never manages to COPY BACK is invisible otherwise
      this.deps.reporter.trackSilentProblem('AndroidSqliteRollbackBackgroundFailed',
        errorReportFields(e));
    }
  }

  /**
   * The DECIDING session for the way back. Asks whether this device should roll back on
   * its NEXT page load, and writes the request marker if so. Copies nothing.
   *
   * Runs after the warm/cold branch because it needs `ConfigDbService` to have loaded.
   * Fire-and-forget and never throws.
   *
   * **The request marker's VALUE is the re-arm generation in force**, and that is the
   * whole reason this session writes it rather than the acting one: the config is loaded
   * here and cannot be read on the boot path, and the generation has to be the one in
   * force when the decision was taken. A rollback that cannot read it refuses to commit
   * rather than guessing — see `RollbackOutcome.ReArmGenerationUnknown`.
   */
  async decideSqliteRollbackAsync(uid: string | undefined): Promise<void> {
    try {
      // only a device that could actually roll back pays for the config wait
      if (!this.deps.isAndroidNative()) return;
      if (this.deps.latchedEngine() !== LocalDatabaseEngine.Sqlite) return;

      const storage = this.deps.localStorageOrNull();
      if (!storage) return;
      // already asked for: nothing to do, and rewriting it would be a needless write
      if (storage.getItem(SQLITE_ROLLBACK_REQUESTED_KEY)) return;
      const blockedMarker = storage.getItem(SQLITE_ROLLBACK_BLOCKED_KEY);
      if (blockedMarker) return;
      if (!uid) return;

      await this.deps.waitForConfigLoadedAsync();

      const shouldRollBack = shouldRequestRollback({
        isAndroidNative: true,
        currentEngine: LocalDatabaseEngine.Sqlite,
        uid,
        blockedMarker,
        controlsSayRollBack: shouldRollBackThisDeviceToIndexedDb(
          this.deps.getConfigValue(ConfigKey.AndroidSqliteRollbackAllowList),
          this.deps.getConfigValue(ConfigKey.AndroidSqliteRollbackToIndexedDbPercent),
          uid,
          this.deps.deviceId(),
          bucketForIdentifier,
        ),
      });
      if (!shouldRollBack) return;

      // `readConfiguredReArmGeneration`, NOT `resolveReArmGeneration`. The two differ only
      // for an unreadable value, and that difference is the whole point: the resolver
      // answers the FLOOR, which is cautious when the number is the generation IN FORCE and
      // is the exact opposite when it is the generation being RECORDED. A recorded floor
      // against the seeded in-force 1 reads as re-armed, so the device would ask for a
      // migration on the very page load its rollback commits — the lap the marker exists to
      // stop. There is no safely-high number to guess instead, so nothing is written and the
      // next deciding session tries again.
      const reArmGeneration = readConfiguredReArmGeneration(
        this.deps.getConfigValue(ConfigKey.AndroidSqliteMigrationRearmGeneration));
      if (reArmGeneration === undefined) {
        this.deps.reporter.trackSilentProblem(
          ROLLBACK_PROBLEM_NAMES[RollbackOutcome.ReArmGenerationUnknown]!, {});
        return;
      }
      storage.setItem(SQLITE_ROLLBACK_REQUESTED_KEY, String(reArmGeneration));
      console.log('SQLite rollback (phase 4): requested; the next page load will copy back');
    } catch (e) {
      // background housekeeping nobody should be interrupted for, but it must not vanish
      // either — a device that never manages to REQUEST is invisible otherwise
      this.deps.reporter.trackSilentProblem('AndroidSqliteRollbackDecideFailed', errorReportFields(e));
    }
  }

  // ─── tearing down ─────────────────────────────────────────────────────────────

  /**
   * Stop every piece of copy work in flight. (#5449)
   *
   * Two things, because the copy can be in either of two states and only one of them
   * is reachable through a handle: a slice that is replicating right now is stopped by
   * its registered canceller, and the slices not yet started are stopped by the flag,
   * which the slice loop checks before each one.
   *
   * Never throws — it is called from the teardown path, whose contract is that it
   * always reaches the reload.
   */
  cancelSqliteMigrationWork(): void {
    this.cancelled = true;
    for (const cancel of this.cancellers) {
      try {
        cancel();
      } catch {
        // a replication that has already finished throws when cancelled, which is the
        // good outcome: there is nothing left to stop. Nothing here may interrupt a
        // teardown whose whole job is removing the previous user's data.
      }
    }
    this.cancellers.clear();
  }

  /**
   * Stop the live follow, if one is running. (#5449)
   *
   * Nothing is awaited by the caller: cancelling is what makes the writing stop, and
   * waiting for handles to close would put a bounded per-database wait in front of a page
   * that is about to disappear anyway.
   */
  stopFollowing(): void {
    void this.follow?.stopAsync();
  }

  /** Register a canceller for a slice replicating right now, and hand back its removal. */
  registerCancellable(cancel: () => void): () => void {
    this.cancellers.add(cancel);
    return () => this.cancellers.delete(cancel);
  }

  /** Has the app started tearing down? Read by the copy loop before each slice. */
  isCancelled(): boolean {
    return this.cancelled;
  }
}
